import React from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import Form from "src/components/base/form";
import { ErrorMessage, Field, FieldGroup, Fieldset, Label, RequiredLabel } from "src/components/base/fieldset";
import { Button, PrimaryButton } from "src/components/base/button";
import { useForm } from "@tanstack/react-form";
import { Combobox, ComboboxOption } from "src/components/base/combobox";
import { ApiInternal } from "src/api/api_internal";
import { FullSplitDnsDomain } from "src/api/generated/frontend_internal_v1";

/**
 * The properties for {@link TenantAddDomainDialog}
 */
export type TenantAddDomainDialogProps = {
    /** Whether the dialog is open */
    isOpen: boolean;
    /** Callback when the popup should be closed */
    onClose: () => void;
    /** Callback when a domain was assigned */
    onSubmit: () => void;
    /** The tenant to assign the domain to */
    tenantId: string;
    /** The domains which are not yet assigned to any tenant */
    domains: Array<FullSplitDnsDomain>;
};

/**
 * Dialog to assign an unassigned split DNS domain to a tenant
 */
export default function TenantAddDomainDialog(props: TenantAddDomainDialogProps) {
    const [t] = useTranslation("tenant-dns");
    const [tg] = useTranslation();

    const [query, setQuery] = React.useState("");

    const form = useForm({
        defaultValues: {
            domain: null as null | FullSplitDnsDomain,
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                if (!value.domain) {
                    return {
                        fields: {
                            domain: t("error.field-must-be-filled"),
                        },
                    };
                }

                const res = await ApiInternal.tenantsDns.addDomain(props.tenantId, value.domain.uuid);
                if (res?.result === "Err") {
                    return {
                        fields: {
                            domain: res.error.domain_conflict ? t("error.domain-conflict") : undefined,
                        },
                    };
                }

                props.onSubmit();
            },
        },
    });

    React.useEffect(() => {
        form.reset();
        setQuery("");
    }, [props.isOpen]);

    const domains = props.domains.filter((domain) => domain.domain.toLowerCase().includes(query.toLowerCase()));

    return (
        <Dialog open={props.isOpen} onClose={props.onClose}>
            <DialogTitle>{t("heading.add-domain")}</DialogTitle>
            <DialogBody>
                <Form onSubmit={form.handleSubmit}>
                    <Fieldset>
                        <FieldGroup>
                            <form.Field name={"domain"}>
                                {(fieldApi) => (
                                    <Field>
                                        <RequiredLabel>{t("label.select-domain")}</RequiredLabel>
                                        <Combobox
                                            value={fieldApi.state.value}
                                            onChange={(v) => fieldApi.handleChange(v)}
                                            onClose={() => setQuery("")}
                                            queryDisplay={(v) => (v !== null ? v.domain : "")}
                                            onQueryChange={(q) => setQuery(q)}
                                            invalid={fieldApi.state.meta.errors.length > 0}
                                        >
                                            {domains.map((domain) => (
                                                <ComboboxOption value={domain} key={domain.uuid}>
                                                    <Label>{domain.domain}</Label>
                                                </ComboboxOption>
                                            ))}
                                        </Combobox>
                                        {fieldApi.state.meta.errors.map((err) => (
                                            <ErrorMessage>{err}</ErrorMessage>
                                        ))}
                                    </Field>
                                )}
                            </form.Field>

                            <DialogActions>
                                <Button plain={true} onClick={props.onClose}>
                                    {tg("button.cancel")}
                                </Button>
                                <PrimaryButton type={"submit"}>{tg("button.save")}</PrimaryButton>
                            </DialogActions>
                        </FieldGroup>
                    </Fieldset>
                </Form>
            </DialogBody>
        </Dialog>
    );
}
